import axios from 'axios'
import { ElMessage } from 'element-plus'

// 获取API基础URL
const getBaseURL = () => {
  const envBaseUrl =
    import.meta?.env?.VITE_API_BASE_URL ||
    process.env?.VITE_API_BASE_URL ||
    process.env?.VUE_APP_API_BASE_URL

  if (envBaseUrl) {
    const trimmed = envBaseUrl.replace(/\/+$/, '')
    const apiUrl = `${trimmed}/api`
    console.log('✅ 使用环境变量:', apiUrl)
    return apiUrl
  }

  // 生产环境临时回退：检测是否在Render前端服务上
  if (window.location.hostname.includes('onrender.com')) {
    // 如果在前端服务上，尝试推断API服务URL
    const frontendUrl = window.location.hostname
    // 假设API服务名包含 'api' 或遵循命名规范
    if (frontendUrl.includes('frontend')) {
      const apiUrl = frontendUrl.replace('frontend', 'api')
      const fullUrl = `https://${apiUrl}/api`
      console.log('✅ 使用URL推断(frontend->api):', fullUrl)
      return fullUrl
    }
    // 其他命名规范的推断
    const fallbackUrl = `https://rofc-api.onrender.com/api`
    console.log('✅ 使用硬编码回退URL:', fallbackUrl)
    return fallbackUrl
  }
  
  console.log('🔧 未找到环境变量，回退到相对路径 /api')
  
  // 默认使用相对路径（本地开发环境）
  return '/api'
}

// 创建axios实例
const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 15000, // 默认 15 秒
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: false
})

// 创建一个专门用于 BlueMap 推送的 axios 实例（超时时间更长）
const apiLongTimeout = axios.create({
  baseURL: getBaseURL(),
  timeout: 150000, // 150 秒，给 BlueMap 渲染足够的时间
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: false
})

// 请求拦截器（通用）
const requestInterceptor = config => {
  // 添加token等认证信息
  const token = localStorage.getItem('auth_token')
  const isGuestMode = localStorage.getItem('guest_mode') === 'true'
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  if (isGuestMode) {
    config.headers['X-Guest-Mode'] = 'true'
  } else {
    delete config.headers['X-Guest-Mode']
  }
  return config
}

const requestErrorInterceptor = error => {
  console.error('请求错误:', error)
  return Promise.reject(new Error(error.message || '请求失败'))
}

// 应用到两个实例
api.interceptors.request.use(requestInterceptor, requestErrorInterceptor)
apiLongTimeout.interceptors.request.use(requestInterceptor, requestErrorInterceptor)

// 全局标志：防止重复跳转到登录页
let isRedirectingToLogin = false

// 跳转到登录页的辅助函数
const redirectToLogin = () => {
  if (isRedirectingToLogin) {
    return // 已经在跳转中，避免重复跳转
  }
  
  isRedirectingToLogin = true
  
  // 清除认证信息
  localStorage.removeItem('auth_token')
  localStorage.removeItem('user_info')
  localStorage.removeItem('guest_mode')
  
  // 如果不在登录页，则跳转
  if (window.location.pathname !== '/login') {
    // 使用replace避免在历史记录中留下当前页面
    window.location.replace('/login')
  } else {
    // 如果已经在登录页，重置标志
    isRedirectingToLogin = false
  }
}

// 响应拦截器（通用）
const responseInterceptor = response => {
  const { data } = response
  if (data.success === false) {
    ElMessage.error(data.message || '请求失败')
    return Promise.reject(new Error(data.message || '请求失败'))
  }
  return data
}

const responseErrorInterceptor = error => {
  console.error('响应错误:', error)

  // 如果正在跳转到登录页，不显示错误消息
  if (isRedirectingToLogin) {
    return new Promise(() => {}) // 中断错误链，防止组件层重复处理错误
  }

  let message = '网络错误'
  let shouldShowError = true
  
  if (error.response) {
    const { status, data } = error.response
    switch (status) {
      case 400:
        message = data.message || '请求参数错误'
        break
      case 401: {
        const isGuestMode = localStorage.getItem('guest_mode') === 'true'
        if (isGuestMode) {
          // 访客模式：只显示提示，不跳转
          message = (data && data.message) || '访客模式访问受限，部分数据需要登录后查看'
          break
        }
        // 非访客模式：跳转到登录页，不显示错误消息
        message = data?.message || '登录已过期，请重新登录'
        shouldShowError = false
        redirectToLogin()
        return new Promise(() => {}) // 中断错误链
      }
      case 403:
        // 兼容性处理：如果后端仍返回403且为无效令牌，也跳转
        if (data && data.message === '无效的访问令牌') {
          redirectToLogin()
          return new Promise(() => {})
        }
        message = '禁止访问'
        break
      case 404:
        message = '资源不存在'
        break
      case 409: {
        shouldShowError = false // 让组件层自己处理提示，避免重复弹窗
        const conflictError = new Error(data.message || '数据冲突，请刷新后重试')
        conflictError.response = error.response // 保留 response 对象供组件判断 status
        return Promise.reject(conflictError)
      }
      case 500:
        message = '服务器内部错误'
        break
      default:
        message = data.message || `请求失败 (${status})`
    }
  } else if (error.code === 'ECONNABORTED') {
    message = '请求超时'
  }

  // 只在需要时显示错误消息
  if (shouldShowError) {
    ElMessage.error(message)
  }

  const wrapped = new Error(message)
  wrapped.status = error?.response?.status
  wrapped.response = error?.response
  wrapped.data = error?.response?.data
  return Promise.reject(wrapped)
}

// 应用到两个实例
api.interceptors.response.use(responseInterceptor, responseErrorInterceptor)
apiLongTimeout.interceptors.response.use(responseInterceptor, responseErrorInterceptor)

// 车站相关API
export const stationApi = {
  // 获取所有车站
  getAll: (params = {}) => api.get('/stations', { params }),

  // 根据ID获取车站
  getById: (id) => api.get(`/stations/${id}`),

  // 创建车站
  create: (data) => api.post('/stations', data),

  // 批量创建车站
  createBatch: (data) => api.post('/stations/batch', data),

  // 更新车站
  update: (id, data) => api.put(`/stations/${id}`, data),

  // 删除车站
  delete: (id) => api.delete(`/stations/${id}`)
}

// 线路相关API
export const lineApi = {
  // 获取所有线路
  getAll: () => api.get('/lines'),

  // 根据ID获取线路
  getById: (id) => api.get(`/lines/${id}`),

  // 获取线路的车站列表
  getStations: (id) => api.get(`/lines/${id}/stations`),

  // 获取线路交路配置
  getRoutes: (id) => api.get(`/lines/${id}/routes`),

  // 获取线路支线配置（含支线车站）
  getBranches: (id) => api.get(`/lines/${id}/branches`),

  // 创建支线
  createBranch: (id, payload) => api.post(`/lines/${id}/branches`, payload),

  // 更新支线配置
  updateBranch: (id, branchId, payload) => api.put(`/lines/${id}/branches/${branchId}`, payload),

  // 更新支线车站（覆盖式）
  updateBranchStations: (id, branchId, stationIds) =>
    api.put(`/lines/${id}/branches/${branchId}/stations`, { station_ids: stationIds }),

  // 删除支线
  deleteBranch: (id, branchId) => api.delete(`/lines/${id}/branches/${branchId}`),

  // 获取线路直通运营配置
  getThroughServices: (id) => api.get(`/lines/${id}/through-services`),

  // 更新线路直通运营配置（覆盖式）
  updateThroughServices: (id, services) => api.put(`/lines/${id}/through-services`, { services }),

  // 创建线路
  create: (data) => api.post('/lines', data),

  // 更新线路
  update: (id, data) => api.put(`/lines/${id}`, data),

  // 删除线路
  delete: (id) => api.delete(`/lines/${id}`),

  // 批量更新线路顺序
  updateOrder: (orders) => api.put('/lines/order', { orders }),

  // 更新线路中车站的顺序
  updateStationOrder: (id, stationIds) => api.put(`/lines/${id}/stations/order`, { station_ids: stationIds }),

  // 添加车站到线路
  addStations: (id, stationIds) => api.post(`/lines/${id}/stations`, { station_ids: stationIds }),

  // 从线路中移除车站
  removeStations: (id, stationIds) => api.delete(`/lines/${id}/stations`, { data: { station_ids: stationIds } }),

  // 更新线路交路配置
  updateRoutes: (id, routes) => api.put(`/lines/${id}/routes`, { routes }),

  // 更新线路详细信息
  updateInfo: (id, data) => api.put(`/lines/${id}/info`, data),

  // 保存线路轨迹点（BlueMap格式的line数组）
  updateTrajectory: (id, points) => api.put(`/lines/${id}/trajectory`, { points })
}

// 运营方相关API
export const operatorApi = {
  getAll: () => api.get('/operators'),
  getById: (id) => api.get(`/operators/${id}`),
  create: (data) => api.post('/operators', data),
  update: (id, data) => api.put(`/operators/${id}`, data),
  delete: (id) => api.delete(`/operators/${id}`),
  getManagers: () => api.get('/operators/managers/all')
}

// 运营负责人API
export const operatorManagerApi = {
  getAll: () => api.get('/operator-managers'),
  create: (data) => api.post('/operator-managers', data),
  update: (id, data) => api.put(`/operator-managers/${id}`, data),
  delete: (id) => api.delete(`/operator-managers/${id}`)
}

// 权限与角色管理
export const accessApi = {
  getPermissions: () => api.get('/access/permissions'),
  getRoles: () => api.get('/access/roles'),
  saveRole: (payload) => api.post('/access/roles', payload),
  updateRolePermissions: (roleId, permissionIds) => api.put(`/access/roles/${roleId}/permissions`, { permission_ids: permissionIds }),
  getUsers: () => api.get('/access/users'),
  updateUserRoles: (userId, roleIds) => api.put(`/access/users/${userId}/roles`, { role_ids: roleIds }),
  updateUserPermissions: (userId, permissions) => api.put(`/access/users/${userId}/permissions`, { permissions })
}

// 访客权限
export const authVisitorApi = {
  getVisitor: () => api.get('/auth/visitor')
}

// 行政区域与建筑物相关 API
export const buildingRegionApi = {
  getAll: () => api.get('/building-regions'),
  getById: (id) => api.get(`/building-regions/${id}`),
  create: (data) => api.post('/building-regions', data),
  update: (id, data) => api.put(`/building-regions/${id}`, data),
  delete: (id) => api.delete(`/building-regions/${id}`)
}

export const regionShapeApi = {
  getByRegion: (regionId) => api.get(`/region-shapes/${regionId}`),
  save: (data) => api.post('/region-shapes', data),
  remove: (regionId) => api.delete(`/region-shapes/${regionId}`)
}

export const buildingApi = {
  getAll: (params = {}) => api.get('/buildings', { params }),
  getById: (id) => api.get(`/buildings/${id}`),
  create: (data) => api.post('/buildings', data),
  update: (id, data) => api.put(`/buildings/${id}`, data),
  delete: (id) => api.delete(`/buildings/${id}`)
}

// 认证相关API
export const authApi = {
  // 用户注册
  register: (userData) => api.post('/auth/register', userData),
  
  // 用户登录
  login: (credentials) => api.post('/auth/login', credentials),
  
  // 获取当前用户信息
  getMe: () => api.get('/auth/me'),
  
  // 更新用户信息
  updateProfile: (userData) => api.put('/auth/profile', userData),
  
  // 修改密码
  changePassword: (passwordData) => api.put('/auth/password', passwordData),
  
  // 验证令牌
  verifyToken: () => api.post('/auth/verify'),
  
  // 管理员：获取所有用户
  getUsers: () => api.get('/auth/users'),
  
  // 管理员：更新用户状态
  updateUser: (id, userData) => api.put(`/auth/users/${id}`, userData)
}

// 审计日志相关API
export const auditLogApi = {
  // 获取所有审计日志（支持筛选和分页）
  getAll: (params = {}) => api.get('/audit-logs', { params }),
  
  // 获取特定记录的历史
  getRecordHistory: (tableName, recordId, limit = 100) => 
    api.get(`/audit-logs/record/${tableName}/${recordId}`, { params: { limit } }),
  
  // 获取特定表的所有变更
  getTableHistory: (tableName, limit = 100) => 
    api.get(`/audit-logs/table/${tableName}`, { params: { limit } }),
  
  // 获取最近的变更
  getRecent: (limit = 50) => api.get('/audit-logs/recent', { params: { limit } }),
  
  // 获取统计信息
  getStats: (days = 30) => api.get('/audit-logs/stats', { params: { days } }),

  // 导出审计日志
  export: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString()
    const url = `${getBaseURL()}/audit-logs/export?${queryString}`
    
    try {
      const token = localStorage.getItem('auth_token')
      const headers = {}
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include'
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('请先登录')
        }
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `导出失败: ${response.status}`)
      }
      
      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/)
        if (matches) {
          filename = decodeURIComponent(matches[1])
        }
      }
      
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
      
      ElMessage.success('导出成功')
    } catch (error) {
      console.error('导出失败:', error)
      ElMessage.error(error.message || '导出失败')
      throw error
    }
  }
}

// 导出相关API
export const exportApi = {
  // 导出BlueMap配置
  exportBlueMap: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString()
    const url = `${getBaseURL()}/export/bluemap?${queryString}`
    
    try {
      // 获取token用于认证
      const token = localStorage.getItem('auth_token')
      const headers = {}
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }
      
      // 使用fetch下载文件
      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include'
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('请先登录')
        }
        throw new Error(`下载失败: ${response.status}`)
      }
      
      // 获取文件名
      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = 'markers.conf'
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/)
        if (matches) {
          filename = matches[1]
        }
      }
      
      // 创建blob并下载
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
      
      ElMessage.success('文件下载成功')
    } catch (error) {
      console.error('下载失败:', error)
      ElMessage.error(error.message || '下载失败')
      throw error
    }
  },

  // 获取BlueMap配置JSON
  getBlueMapJson: (params = {}) => api.get('/export/bluemap', {
    params: { ...params, format: 'json' }
  }),

  // 获取统计信息（可自定义请求配置，如 timeout）
  getStats: (config = {}) => api.get('/export/stats', config),

  // 推送配置到 BlueMap 服务器（使用长超时实例）
  pushToBlueMap: (params = {}) => apiLongTimeout.post('/export/push-to-bluemap', params)
}

// 资源包管理
export const resourcePackApi = {
  list: () => apiLongTimeout.get('/resource-packs'),
  upload: (file, target = 'auto', onProgress = null) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('target', target)
    
    // 使用长超时实例，并支持上传进度回调
    return apiLongTimeout.post('/resource-packs/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000, // 10分钟超时，足够上传大文件
      onUploadProgress: onProgress ? (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(percentCompleted, progressEvent.loaded, progressEvent.total)
      } : undefined
    })
  },
  saveIndex: (items, header = null, etag = null) => api.put('/resource-packs', { items, header, etag }),
  deleteFile: ({ url, key }) => api.delete('/resource-packs/file', { data: { url, key } }),
  cleanup: (urls = []) => api.post('/resource-packs/cleanup', { urls }),
  getMessage: () => api.get('/resource-packs/message'),
  saveMessage: (content) => api.put('/resource-packs/message', { content })
}

// 时刻表相关API
export const timetableAPI = {
  // 站间运行时间
  getTravelTimes: (lineId) => api.get(`/timetable/travel-times/${lineId}`),
  updateTravelTimes: (lineId, travelTimes) => api.put(`/timetable/travel-times/${lineId}`, { travelTimes }),

  // 交路配置
  getPatterns: (lineId) => api.get(`/timetable/patterns/${lineId}`),
  getPatternDetail: (patternId) => api.get(`/timetable/patterns/detail/${patternId}`),
  createPattern: (pattern, stops) => api.post('/timetable/patterns', { pattern, stops }),
  updatePattern: (patternId, pattern, stops) => api.put(`/timetable/patterns/${patternId}`, { pattern, stops }),
  deletePattern: (patternId) => api.delete(`/timetable/patterns/${patternId}`),

  // 车次
  getPatternTrips: (patternId) => api.get(`/timetable/trips/pattern/${patternId}`),
  getLineTrips: (lineId) => api.get(`/timetable/trips/line/${lineId}`),
  batchCreateTrips: (trips) => api.post('/timetable/trips/batch', { trips }),
  deleteTrip: (tripId) => api.delete(`/timetable/trips/${tripId}`),
  deletePatternTrips: (patternId) => api.delete(`/timetable/trips/pattern/${patternId}`),

  // 运行图
  createTimetable: (timetableData) => api.post('/timetable/timetables', timetableData),
  getLineTimetables: (lineId) => api.get(`/timetable/timetables/${lineId}`)
}

// AI 调试（本地模型 + MCP 工具）
export const aiApi = {
  chat: (data) => apiLongTimeout.post('/ai/chat', data),
  tools: () => api.get('/ai/tools')
}

// 健康检查
export const healthCheck = () => api.get('/health')

// 获取完整的图片URL
export const getImageUrl = (relativePath) => {
  if (!relativePath) return ''
  
  // 如果已经是完整URL，直接返回
  if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
    return relativePath
  }
  
  // 获取API基础URL（去掉/api后缀）
  const apiBaseUrl = getBaseURL().replace('/api', '')
  
  // 如果API基础URL是相对路径，使用localhost:3000
  if (apiBaseUrl === '/api' || apiBaseUrl === '') {
    return `http://localhost:3000${relativePath}`
  }
  
  // 否则拼接完整的URL
  return `${apiBaseUrl}${relativePath}`
}

export default api
